/**
 * Renders a message's attachments - photos, videos, audio, and plain files -
 * with a save control under each one.
 *
 * Shared by the full conversation (pages/Thread.tsx) and the expanded inbox
 * row (pages/messages/ThreadRow.tsx) so an attachment NEVER silently
 * disappears depending on which surface a message is read on. Bobbie hit
 * exactly that: the inbox row rendered text only, so "Test image" showed its
 * words and dropped its photo - "the image is no longer there. they need to
 * remain in the conversation."
 *
 * `compact` is the inbox-row variant: smaller media, so six recent messages
 * with photos do not turn the merged inbox into a wall of images.
 */

import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon';
import { MediaEmbedPlayer } from './MediaEmbedPlayer';
import { AudioWithSpeed } from './PlaybackSpeedControl';
import { MessagePhotoViewer } from './MessagePhotoViewer';
import {
  attachmentFileName,
  deliverPreparedBlobToDevice,
  openInNewTab,
  prepareAttachmentForDevice,
  safeRenderableAttachmentUrl,
  SaveNeedsFreshTapError,
  type PreparedBlobSave,
  type SaveBlobOutcome,
} from '../lib/saveAttachment';
import { requestAttachmentDownloadUrl } from '../lib/attachmentDownloadLink';
import { PREPARED_BLOB_TTL_MS, prepareAndDeliverCurrentSave } from '../lib/saveLifecycle';
import {
  finishPreparedVideoSave,
  prepareVideoForDevice,
  type ShareSheetFallback,
} from '../lib/saveVideo';
import {
  freshTapButtonLabel,
  freshTapPrompt,
  SAVE_OUTCOME_COPY,
  SHARE_SHEET_FALLBACK_COPY,
} from '../lib/saveVideoCopy';
import { useSaveBusy } from '../lib/useSaveBusy';
import type { MessageAttachment } from '../lib/threads';

const gridStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginTop: 6,
};

function imageStyle(compact: boolean): React.CSSProperties {
  return {
    display: 'block',
    width: `min(${compact ? 180 : 260}px, 100%)`,
    maxHeight: compact ? 180 : 260,
    objectFit: 'cover',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(0,0,0,0.12)',
    // Solid, NOT semi-transparent: a see-through backing let the sender's
    // lime bubble bleed through any photo with transparency (a logo PNG
    // most of all), washing the whole image Studio Green.
    background: 'var(--white)',
  };
}

// Wraps MediaEmbedPlayer rather than styling a <video> directly - the
// player owns its own element (a Play button first, a <video> only once
// tapped) and its own sizing (100% width, maxHeight 320, its own
// border-radius and background). This just keeps the same compact/full
// WIDTH cap the raw <video> used to carry, so a message thread's video
// does not grow to the full bubble width the way a photo does.
function videoWrapStyle(compact: boolean): React.CSSProperties {
  return {
    width: `min(${compact ? 240 : 320}px, 100%)`,
  };
}

const fileChipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  maxWidth: '100%',
  padding: '10px 12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--line)',
  background: 'var(--white)',
  color: 'var(--ink)',
  fontSize: 13,
  fontWeight: 700,
  textDecoration: 'none',
};

const saveBtnStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  minHeight: 'var(--tap-target)',
  padding: '4px 12px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid rgba(0,0,0,0.15)',
  background: 'var(--white)',
  color: 'var(--ink)',
  fontSize: 12,
  fontWeight: 800,
  cursor: 'pointer',
};

function attachmentNoun(attachment: MessageAttachment): string {
  if (attachment.kind === 'video') return 'video';
  if (attachment.kind === 'file') {
    return attachment.contentType.startsWith('audio/') ? 'audio' : 'file';
  }
  return 'photo';
}

/**
 * "Save photo" / "Save video" / "Save audio" / "Save file" - Bobbie: "we
 * need to be able to download the photo right there. and save it." On
 * iPhone this opens the share sheet (Save Image -> camera roll); on a
 * desktop it downloads the file. See lib/saveAttachment.ts for the ladder.
 *
 * A failed save NEVER opens a tab by itself: an asynchronous fetch failure
 * that navigates on its own is the surprise this whole feature exists to
 * stop. It offers the tab instead, as a control the reader taps, because on
 * iOS opening the original and pressing and holding it is still the working
 * route to the camera roll when the blob fetch cannot run. Same hatch, same
 * words, as the video lane's "Open the original link"
 * (components/dancer/DancerMediaRow.tsx).
 *
 * Videos, audio and documents (Bobbie, 2026-10-23: "no one can save videos
 * ... especially when sent via message") go through lib/saveVideo.ts's
 * Firebase route with the signed download link (lib/attachmentDownloadLink.ts):
 * a computer or Android phone downloads the signed link directly, and an
 * iPhone tries the share sheet first and falls back to that same download.
 * They used to fetch() the bytes from the page, which the bucket's missing
 * CORS policy refuses every time, so every such save failed. Photos keep
 * their own ladder, which already has a same-origin recovery
 * (photoDownload.mjs) and only ever needs a few megabytes.
 *
 * The copy does NOT tell anyone to ask the studio to resend it. A board
 * photo posted by another parent, or a photo in a family thread, was never
 * the studio's to resend, and a resend fixes nothing when the network is
 * what failed.
 */
export function AttachmentSaveButton({ attachment }: { attachment: MessageAttachment }) {
  const [failed, setFailed] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [done, setDone] = useState<SaveBlobOutcome | null>(null);
  const [fallback, setFallback] = useState<ShareSheetFallback | null>(null);
  const [prepared, setPrepared] = useState<{
    identity: string;
    save: PreparedBlobSave;
  } | null>(null);
  const noun = attachmentNoun(attachment);
  const saveIdentity = JSON.stringify([
    attachment.id,
    attachment.url,
    attachment.storagePath ?? '',
    attachment.name,
    attachment.contentType,
  ]);
  const currentIdentityRef = useRef(saveIdentity);
  currentIdentityRef.current = saveIdentity;
  // Busy exactly while a save for the item now shown is still running, so
  // one tap saves once even if the item changes and changes back
  // (lib/useSaveBusy.ts).
  const { busy, beginAttempt, endAttempt } = useSaveBusy(saveIdentity);

  useEffect(() => {
    setPrepared(null);
    setFailed(false);
    setBlocked(false);
    setDone(null);
    setFallback(null);
  }, [saveIdentity]);

  useEffect(() => {
    if (!prepared) return undefined;
    const timer = window.setTimeout(() => setPrepared(null), PREPARED_BLOB_TTL_MS);
    return () => window.clearTimeout(timer);
  }, [prepared]);

  return (
    <>
      <button
        type="button"
        title={`Save this ${noun} to your device`}
        style={{ ...saveBtnStyle, opacity: busy ? 0.6 : 1 }}
        disabled={busy}
        onClick={async () => {
          const attemptIdentity = saveIdentity;
          const attempt = beginAttempt();
          setFailed(false);
          setBlocked(false);
          setDone(null);
          setFallback(null);
          try {
            if (prepared && prepared.identity !== saveIdentity) setPrepared(null);
            if (prepared?.identity === saveIdentity) {
              const next = prepared.save;
              setPrepared(null);
              const outcome = await deliverPreparedBlobToDevice(next);
              if (currentIdentityRef.current !== attemptIdentity) return;
              setDone(outcome);
              return;
            }

            const deliverBlob = async (next: PreparedBlobSave) => {
              try {
                return await deliverPreparedBlobToDevice(next, { deferOnNotAllowed: true });
              } catch (caught) {
                if (caught instanceof SaveNeedsFreshTapError) {
                  setPrepared({ identity: attemptIdentity, save: next });
                  return null;
                }
                throw caught;
              }
            };
            let outcome: SaveBlobOutcome | null = null;
            let deliveredFallback: ShareSheetFallback | null = null;
            const delivery = attachment.kind === 'image'
              ? await prepareAndDeliverCurrentSave({
                attemptIdentity,
                currentIdentity: () => currentIdentityRef.current,
                prepare: () => prepareAttachmentForDevice(attachment),
                deliver: async (next) => { outcome = await deliverBlob(next); },
              })
              : await prepareAndDeliverCurrentSave({
                attemptIdentity,
                currentIdentity: () => currentIdentityRef.current,
                prepare: () => prepareVideoForDevice({
                  link: { url: attachment.url },
                  fileName: attachmentFileName(attachment.name, attachment.contentType),
                  contentType: attachment.contentType,
                  // A message attachment is never R2-homed.
                  presignR2Download: () => Promise.reject(new Error('not an R2 file')),
                  signFirebaseDownload: (url, fileName) => requestAttachmentDownloadUrl({
                    url,
                    fileName,
                    storagePath: attachment.storagePath,
                  }),
                }),
                deliver: async (next) => {
                  if (next.route !== 'blob') {
                    // A signed download link, clicked in this same tap.
                    outcome = await finishPreparedVideoSave(next);
                    deliveredFallback = next.shareSheetFallback ?? null;
                    return;
                  }
                  outcome = await deliverBlob({
                    blob: next.blob,
                    fileName: next.fileName,
                    ...(next.contentType ? { declaredContentType: next.contentType } : {}),
                  });
                },
              });
            if (delivery.status === 'stale') return;
            if (currentIdentityRef.current !== attemptIdentity) return;
            setDone(outcome);
            setFallback(deliveredFallback);
          } catch {
            // SaveNeedsFreshTapError never reaches here: the first tap's
            // deliver catches it and holds the bytes, and the fresh tap
            // delivers without deferOnNotAllowed, so it cannot throw it.
            if (currentIdentityRef.current !== attemptIdentity) return;
            setFailed(true);
          } finally {
            endAttempt(attempt);
          }
        }}
      >
        <Icon name="download" size={14} />
        {busy ? 'Saving...' : prepared ? freshTapButtonLabel(noun) : `Save ${noun}`}
      </button>
      {prepared && (
        <span role="status" style={{ fontSize: 11, color: 'var(--ink-soft)' }}>
          {freshTapPrompt(noun)}
        </span>
      )}
      {done && SAVE_OUTCOME_COPY[done] && (
        <span role="status" style={{ fontSize: 11, color: 'var(--ink-soft)', fontWeight: 700 }}>
          {SAVE_OUTCOME_COPY[done]}
        </span>
      )}
      {done === 'downloaded' && fallback && noun === 'video' && (
        <span role="status" style={{ fontSize: 11, color: 'var(--ink-soft)', fontWeight: 700 }}>
          {SHARE_SHEET_FALLBACK_COPY[fallback]}
        </span>
      )}
      {failed && (
        <>
          <span style={{ fontSize: 11, color: 'var(--rose-deep)' }}>
            Couldn't save this {noun}. Try Save again, or open the original and press and hold it to save.
          </span>
          <button
            type="button"
            style={{ ...saveBtnStyle, fontSize: 11 }}
            onClick={() => { if (!openInNewTab(attachment.url)) setBlocked(true); }}
          >
            <Icon name="link" size={14} />
            Open the original {noun}
          </button>
          {blocked && (
            <span style={{ fontSize: 11, color: 'var(--rose-deep)' }}>
              Your browser blocked that tab. Allow pop-ups for the Hub and try again.
            </span>
          )}
        </>
      )}
    </>
  );
}

export function MessageAttachments({
  attachments,
  compact = false,
  photoLayout = 'stack',
  showInlinePhotoSave = true,
}: {
  attachments: MessageAttachment[];
  compact?: boolean;
  photoLayout?: 'stack' | 'grid';
  showInlinePhotoSave?: boolean;
}) {
  const [openPhotoId, setOpenPhotoId] = useState<string | null>(null);
  const photos = attachments.flatMap(attachment => {
    const url = attachment.kind === 'image' ? safeRenderableAttachmentUrl(attachment) : null;
    return url ? [{ ...attachment, url }] : [];
  });
  const photoIndex = photos.findIndex(photo => photo.id === openPhotoId);
  if (attachments.length === 0) return null;
  return (
    <div style={photoLayout === 'grid' ? {
      display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 6,
    } : gridStyle}>
      {attachments.map((attachment) => {
        const safeUrl = safeRenderableAttachmentUrl(attachment);
        if (!safeUrl) {
          return (
            <div
              key={attachment.id || attachment.storagePath || attachment.name}
              role="status"
              style={{ ...fileChipStyle, alignSelf: 'flex-start', color: 'var(--ink-soft)' }}
            >
              <Icon name="lock" size={16} />
              <span>{attachment.name ? `${attachment.name}: ` : ''}Attachment unavailable</span>
            </div>
          );
        }
        const safeAttachment = { ...attachment, url: safeUrl };
        return (
        <div
          key={attachment.id || attachment.storagePath || safeUrl}
          style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          {attachment.kind === 'video' ? (
            // The shared player (packet J20: one player everywhere, never a
            // second). It is click-to-load rather than the old block's
            // eager <video> - the same no-autoplay intent that comment used
            // to carry (a message thread auto-playing sound/motion for
            // everyone who opens it is exactly the surprise a "quick clip
            // in a conversation" should not be), just enforced by needing a
            // tap at all rather than by the controls alone. playsInline and
            // the Save control below are both still there, same as before.
            <div style={videoWrapStyle(compact)}>
              <MediaEmbedPlayer
                url={safeUrl}
                title={attachment.name || 'Message video attachment'}
                kindHint="video"
              />
            </div>
          ) : attachment.kind === 'file' ? (
            attachment.contentType.startsWith('audio/') ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ ...fileChipStyle, alignSelf: 'flex-start' }} title={attachment.name}>
                  <Icon name="music" size={16} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {attachment.name || 'Audio'}
                  </span>
                </span>
                {/* The shared Speed control rides under it (0.75x, 0.85x,
                    Normal), the same one every Hub player carries. */}
                <AudioWithSpeed
                  src={safeUrl}
                  preload="metadata"
                  style={{ width: '100%', maxWidth: compact ? 220 : 280 }}
                  title={attachment.name || 'Message audio attachment'}
                />
              </div>
            ) : (
              <a
                href={safeUrl}
                target="_blank"
                rel="noreferrer noopener"
                style={{ ...fileChipStyle, alignSelf: 'flex-start' }}
                title={attachment.name}
              >
                <Icon name="clipboard" size={16} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {attachment.name || 'File'}
                </span>
              </a>
            )
          ) : (
            <div style={photoLayout === 'grid' ? {
              aspectRatio: '1', overflow: 'hidden', borderRadius: 'var(--radius-md)', border: '1px solid var(--line)', background: 'var(--silver-panel)',
            } : undefined}>
              <button
                type="button"
                aria-label={`Open photo: ${attachment.name || 'Message attachment'}`}
                onClick={() => setOpenPhotoId(attachment.id)}
                style={{
                  color: 'inherit', alignSelf: 'flex-start', border: 0, padding: 0,
                  background: 'transparent', cursor: 'pointer', minHeight: 'var(--tap-target)',
                  minWidth: 'var(--tap-target)', boxShadow: 'none', borderRadius: 0,
                  ...(photoLayout === 'grid' ? { display: 'block', width: '100%', height: '100%' } : {}),
                }}
                title={attachment.name}
              >
                <img
                  src={safeUrl}
                  alt={attachment.name || 'Message attachment'}
                  style={photoLayout === 'grid'
                    ? { display: 'block', width: '100%', height: '100%', objectFit: 'cover' }
                    : imageStyle(compact)}
                  loading="lazy"
                />
              </button>
            </div>
          )}
          {(attachment.kind !== 'image' || showInlinePhotoSave) && <AttachmentSaveButton attachment={safeAttachment} />}
        </div>
        );
      })}
      {photoIndex >= 0 && <MessagePhotoViewer photos={photos} index={photoIndex}
        onChange={index => setOpenPhotoId(photos[index].id)} onClose={() => setOpenPhotoId(null)}
        saveControl={<AttachmentSaveButton key={photos[photoIndex].id} attachment={photos[photoIndex]} />} />}
    </div>
  );
}

export default MessageAttachments;
